import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSnackbar } from 'notistack'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  GuardaPrivacidade,
  mensagemErroPrivacidade,
  rotuloAtor,
  useAcessoPrivacidade,
} from '@/components/privacidade/comum'
import { privacidadeService } from '@/services/privacidadeService'
import {
  CATEGORIAS_RETENCAO,
  PoliticaRetencao,
  PoliticasRetencao,
  rotuloCategoria,
} from '@/types/privacidade'
import { formatDateTime } from '@/utils/formatters'

const rotuloPrazo = (p: PoliticaRetencao): string =>
  p.prazoDias == null ? 'Sem prazo (nunca propõe)' : `${p.prazoDias} dias após a remoção lógica`

const TabelaPoliticas: FC<{ linhas: PoliticaRetencao[]; rotulo: string; eu: string | null }> = ({
  linhas,
  rotulo,
  eu,
}) => (
  <TableContainer component={Paper} elevation={0}>
    <Table size="small" aria-label={rotulo}>
      <TableHead>
        <TableRow>
          <TableCell>Categoria</TableCell>
          <TableCell>Tipo de documento</TableCell>
          <TableCell>Prazo</TableCell>
          <TableCell>Expurgável</TableCell>
          <TableCell>Versão</TableCell>
          <TableCell>Vigente desde</TableCell>
          <TableCell>Justificativa</TableCell>
          <TableCell>Autor</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {linhas.map((p) => (
          <TableRow key={p.id}>
            <TableCell>{rotuloCategoria(p.categoria)}</TableCell>
            <TableCell>{p.tipoDocumento ?? 'Todos'}</TableCell>
            <TableCell>{rotuloPrazo(p)}</TableCell>
            <TableCell>
              {p.expurgavel ? (
                <Chip size="small" label="Sim" variant="outlined" />
              ) : (
                <Chip size="small" color="warning" label="Não — obrigação de retenção" />
              )}
            </TableCell>
            <TableCell>v{p.versao}</TableCell>
            <TableCell>{formatDateTime(p.vigenteDesde)}</TableCell>
            <TableCell sx={{ maxWidth: 280 }}>{p.justificativa}</TableCell>
            <TableCell>{rotuloAtor(p.criadaPor, eu)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

/** Estado do formulário; o prazo fica em texto à parte (vazio = sem prazo). */
interface FormPolitica {
  categoria: string
  tipoDocumento: string
  expurgavel: boolean
  justificativa: string
}

const POLITICA_VAZIA: FormPolitica = {
  categoria: 'OPORTUNIDADE',
  tipoDocumento: '',
  expurgavel: true,
  justificativa: '',
}

const Conteudo: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [dados, setDados] = useState<PoliticasRetencao | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [verHistorico, setVerHistorico] = useState(false)

  const [form, setForm] = useState<FormPolitica | null>(null)
  const [prazoTexto, setPrazoTexto] = useState('')
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setDados(await privacidadeService.politicas())
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar a política de retenção'))
      setDados(null)
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const abrirNova = (base?: PoliticaRetencao) => {
    setForm(
      base
        ? {
            categoria: base.categoria,
            tipoDocumento: base.tipoDocumento ?? '',
            expurgavel: base.expurgavel,
            justificativa: '',
          }
        : { ...POLITICA_VAZIA }
    )
    setPrazoTexto(base?.prazoDias != null ? String(base.prazoDias) : '')
  }

  const prazoInvalido = prazoTexto.trim() !== '' && !(Number.isInteger(Number(prazoTexto)) && Number(prazoTexto) > 0)

  const salvar = async () => {
    if (!form) return
    setSalvando(true)
    try {
      // Sem prazo/tipo: campo AUSENTE (o servidor não conhece `marco`; só REMOCAO_LOGICA).
      await privacidadeService.novaPolitica({
        categoria: form.categoria,
        ...(form.tipoDocumento.trim() ? { tipoDocumento: form.tipoDocumento.trim() } : {}),
        ...(prazoTexto.trim() ? { prazoDias: Number(prazoTexto) } : {}),
        expurgavel: form.expurgavel,
        justificativa: form.justificativa.trim(),
      })
      enqueueSnackbar('Nova versão da política registrada', { variant: 'success' })
      setForm(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao registrar a política'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Política de retenção
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Por categoria de documento, versionada. Sem prazo cadastrado, nada é proposto para expurgo — nenhum
            prazo legal nasce preenchido sem validação do Jurídico/DPO.
          </Typography>
        </Box>
        {acesso.dpo && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirNova()} sx={{ flexShrink: 0 }}>
            Nova versão
          </Button>
        )}
      </Stack>
      {!acesso.dpo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Somente o DPO altera a política. Você está em modo leitura.
        </Alert>
      )}

      {carregando && !dados ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : dados && dados.vigentes.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma política cadastrada"
          detalhe="Todas as categorias estão em “Sem prazo (nunca propõe)”."
        />
      ) : dados ? (
        <Stack spacing={2}>
          <TabelaPoliticas linhas={dados.vigentes} rotulo="Políticas vigentes" eu={acesso.usuarioId} />
          {dados.historico.length > 0 && (
            <Box>
              <Button size="small" onClick={() => setVerHistorico((v) => !v)} aria-expanded={verHistorico}>
                {verHistorico ? 'Ocultar histórico' : `Histórico de versões (${dados.historico.length})`}
              </Button>
              {verHistorico && (
                <TabelaPoliticas linhas={dados.historico} rotulo="Histórico de versões" eu={acesso.usuarioId} />
              )}
            </Box>
          )}
        </Stack>
      ) : null}

      <Dialog open={!!form} onClose={() => (salvando ? undefined : setForm(null))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Nova versão da política</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Categoria"
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              >
                {CATEGORIAS_RETENCAO.map((c) => (
                  <MenuItem key={c.valor} value={c.valor}>
                    {c.rotulo}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tipo de documento (opcional)"
                helperText="Refina a categoria; vazio = todos os tipos."
                value={form.tipoDocumento}
                onChange={(e) => setForm({ ...form, tipoDocumento: e.target.value })}
              />
              <TextField
                label="Prazo em dias (contado da remoção lógica)"
                value={prazoTexto}
                onChange={(e) => setPrazoTexto(e.target.value)}
                error={prazoInvalido}
                helperText={prazoInvalido ? 'Número inteiro maior que zero, ou vazio.' : 'Vazio = sem prazo (nunca propõe expurgo).'}
                inputProps={{ inputMode: 'numeric' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.expurgavel}
                    onChange={(e) => setForm({ ...form, expurgavel: e.target.checked })}
                  />
                }
                label={form.expurgavel ? 'Expurgável' : 'Não expurgável (obrigação de retenção)'}
              />
              <TextField
                label="Justificativa"
                required
                multiline
                rows={3}
                value={form.justificativa}
                onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                inputProps={{ maxLength: 500 }}
              />
              <Typography variant="caption" color="text.secondary">
                A versão anterior continua no histórico; nada é apagado. A política só propõe — todo expurgo
                passa por duas aprovações.
              </Typography>
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForm(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={salvando || !form?.justificativa.trim() || prazoInvalido}
          >
            {salvando ? 'Salvando…' : 'Registrar versão'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/** Política de retenção (AN-12 §3): leitura ADMIN e DPO; escrita só DPO. */
export const PoliticasRetencaoPage: FC = () => (
  <GuardaPrivacidade>
    <Conteudo />
  </GuardaPrivacidade>
)

export default PoliticasRetencaoPage
